import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { Facility } from '../../interfaces/facility';
import * as FacilityActions from './facilities.actions';

export const facilitiesFeatureKey = 'facilities';

export interface State extends EntityState<Facility> {
  // additional entities state properties
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: any;
}

export const adapter: EntityAdapter<Facility> = createEntityAdapter<Facility>();

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
});

export const facilityReducer = createReducer(
  initialState,
  on(FacilityActions.loadFacilities, (state) => ({ ...state, loading: true })),
  on(FacilityActions.loadFacilitiesSuccess, (state, action) => {
    const facilities = action.facilities.map((facility) => ({ ...facility, headquarters: false }));
    if (facilities[0]) {
      facilities[0].headquarters = true;
    }
    return adapter.upsertMany(facilities, { ...state, loading: false });
  }),
  on(FacilityActions.loadFacilitiesFailure, (state, action) => ({ ...state, error: action.error, loading: false })),
  on(FacilityActions.createFacility, (state, action) => ({ action, ...state, creating: true })),
  on(FacilityActions.createFacilitySuccess, (state, action) => {
    let newState = state;
    if (action.facility.headquarters) {
      newState = adapter.map((facility) => ({ ...facility, headquarters: false }), state);
    }
    return adapter.upsertOne(action.facility, { ...newState, creating: false });
  }),
  on(FacilityActions.createFacilityFailure, (state, action) => ({ ...state, error: action.error, creating: false })),
  on(FacilityActions.updateFacility, (state, action) => ({ action, ...state, updating: true })),
  on(FacilityActions.updateFacilitySuccess, (state, action) => {
    let newState = state;
    if (action.facility.headquarters) {
      newState = adapter.map((facility) => ({ ...facility, headquarters: false }), state);
    }
    return adapter.upsertOne(action.facility, { ...newState, updating: false });
  }),
  on(FacilityActions.updateFacilityFailure, (state, action) => ({ ...state, error: action.error, updating: false })),
  on(FacilityActions.deleteFacility, (state) => ({ ...state, deleting: true })),
  on(FacilityActions.deleteFacilitySuccess, (state, action) =>
    adapter.removeOne(action.id, { ...state, deleting: true })
  ),
  on(FacilityActions.deleteFacilityFailure, (state, action) => ({ ...state, error: action.error, deleting: true })),
  on(FacilityActions.clearFacilities, (state) => adapter.removeAll(state))
);

export const selectFacilityState = createFeatureSelector<State>(facilitiesFeatureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectFacilityState);

export const selectFacilitiesAll = selectAll;
export const selectFacilitiesTotal = selectTotal;
export const selectFacilitiesEntities = selectEntities;
export const selectFacilitiesIds = selectIds;

export const selectFacilitiesLoading = createSelector(selectFacilityState, (state) => state.loading);
export const selectFacilitiesHeadquarter = createSelector(selectAll, (facilities) =>
  facilities.find((facility) => facility.headquarters)
);
export const selectFacilitiesStandard = createSelector(selectAll, (facilities) =>
  facilities.filter((facility) => !facility.headquarters)
);
